import { ColumnDef } from "@tanstack/react-table";
import ExpandableCountMultiCell from "./ExpandableCountMultiCell";

export interface BuildExpandableCountMultiColumnProps<T> {
  panelId: string;
  accessorKey?: string;
  id?: string;
  header: string;
  getCount: (row: T) => number;
  getTotal?: (row: T) => number;
  expanded: Record<string, string | null>;
  onExpand: (rowId: string, panelId: string | null) => void;
  showExpandIcon?: boolean;
  className?: string;
  size?: number;
  minSize?: number;
  autoExpandRowId?: string | null;
  onAutoExpandComplete?: () => void;
}

/**
 * Creates a column that displays a count and controls multi-panel row expansion.
 *
 * Use this when a table has **two or more** expandable columns that each show
 * different content (e.g. Notes + Changes, Notes + Program Levels). Each column
 * gets its own `panelId`; the consumer's `renderExpandedRow` routes to the
 * correct panel based on `expanded[row.id]`.
 *
 * For single-column expansion, use `buildExpandableCountColumn` instead.
 *
 * @example
 * // State in consumer component
 * const [expanded, setExpanded] = useState<Record<string, string | null>>({});
 * const onExpand = useCallback((rowId: string, panelId: string | null) => {
 *   setExpanded(prev => ({ ...prev, [rowId]: panelId }));
 * }, []);
 *
 * // Column definitions
 * const columns = [
 *   buildExpandableCountMultiColumn<Row>({
 *     panelId: "notes",
 *     accessorKey: "notes",
 *     header: "Notes",
 *     getCount: (row) => row.notes.length,
 *     expanded,
 *     onExpand
 *   }),
 *   buildExpandableCountMultiColumn<Row>({
 *     panelId: "changes",
 *     accessorKey: "changes",
 *     header: "Changes",
 *     getCount: (row) => row.changes.length,
 *     expanded,
 *     onExpand
 *   })
 * ];
 *
 * // Render expanded row
 * <Table
 *   renderExpandedRow={({ row }) => {
 *     if (isCompactLayout) {
 *       // Mobile: stack all panels
 *       return (
 *         <>
 *           <NotesPanel row={row} />
 *           <ChangesPanel row={row} />
 *         </>
 *       );
 *     }
 *     // Desktop: show active panel
 *     switch (expanded[row.id]) {
 *       case "notes": return <NotesPanel row={row} />;
 *       case "changes": return <ChangesPanel row={row} />;
 *       default: return <></>;
 *     }
 *   }}
 * />
 */
export function buildExpandableCountMultiColumn<T>({
  panelId,
  accessorKey,
  id,
  header,
  getCount,
  getTotal,
  expanded,
  onExpand,
  showExpandIcon = true,
  className,
  size,
  minSize,
  autoExpandRowId,
  onAutoExpandComplete
}: BuildExpandableCountMultiColumnProps<T>): ColumnDef<T> {
  return {
    ...(accessorKey ? { accessorKey } : {}),
    id: id ?? accessorKey,
    header,
    enableSorting: false,
    size,
    minSize: minSize ?? size,
    cell: ({ row }) => {
      const count = getCount(row.original);
      const total = getTotal ? getTotal(row.original) : undefined;
      const hasItems = total !== undefined ? total > 0 : count > 0;

      return (
        <ExpandableCountMultiCell
          row={row}
          count={count}
          total={total}
          hasItems={hasItems}
          panelId={panelId}
          expanded={expanded}
          onExpand={onExpand}
          showExpandIcon={showExpandIcon}
          className={className}
          autoExpandRowId={autoExpandRowId}
          onAutoExpandComplete={onAutoExpandComplete}
        />
      );
    },
    meta: {
      headerClassName: "max-sm:inline"
    }
  };
}
